import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, Typography, Alert, CircularProgress, Button } from '@mui/material';

/**
 * EwayPayNowForm - eWay Pay Now Button with proper server-side verification
 *
 * This component uses eWay's Pay Now Button with the data-resulturl feature
 * to properly verify payments on the server before creating appointments.
 *
 * Flow:
 * 1. Save booking data to sessionStorage
 * 2. Render Pay Now button with data-resulturl pointing to callback
 * 3. User completes payment in eWay's modal
 * 4. eWay redirects to callback URL with AccessCode
 * 5. Callback page verifies payment via API and creates appointment
 */
const EwayPayNowForm = ({
  paymentData,
  onPaymentComplete,
  onPaymentError,
  ewayConfig
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [buttonReady, setButtonReady] = useState(false);
  const containerRef = useRef(null);
  const observerRef = useRef(null);
  const paymentProcessedRef = useRef(false);
  const verificationAttemptedRef = useRef(false);

  console.log('eWay Pay Now Form Config received:', ewayConfig);
  console.log('Payment Data:', paymentData);

  // Generate a unique booking reference for this transaction
  const bookingRef = useRef(`booking_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);

  // Store booking data in sessionStorage for verification callback
  const storeBookingData = useCallback(() => {
    const bookingData = {
      ref: bookingRef.current,
      clinic_id: paymentData.clinic_id,
      practitioner_id: paymentData.practitioner_id,
      appointment_type_id: paymentData.appointment_type_id,
      starts_at: paymentData.starts_at,
      ends_at: paymentData.ends_at,
      patient_first_name: paymentData.patient_first_name,
      patient_last_name: paymentData.patient_last_name,
      patient_email: paymentData.patient_email,
      patient_phone: paymentData.patient_phone,
      notes: paymentData.notes,
      amount: paymentData.amount,
      service_name: paymentData.service_name || paymentData.appointment_type_name,
      practitioner_name: paymentData.practitioner_name,
      timestamp: Date.now()
    };

    sessionStorage.setItem('pending_eway_booking', JSON.stringify(bookingData));
    console.log('Stored booking data for verification:', bookingData);
  }, [paymentData]);

  // Verify payment with the server
  const verifyPayment = useCallback(async (accessCode) => {
    if (verificationAttemptedRef.current) {
      console.log('Verification already attempted, skipping');
      return;
    }
    verificationAttemptedRef.current = true;

    console.log('=== VERIFYING PAYMENT WITH SERVER ===');
    console.log('AccessCode:', accessCode);

    setVerifying(true);

    try {
      // Get stored booking data
      const storedData = sessionStorage.getItem('pending_eway_booking');
      if (!storedData) {
        throw new Error('No booking data found in session');
      }

      const bookingData = JSON.parse(storedData);
      console.log('Retrieved booking data:', bookingData);

      // Call verification endpoint
      const response = await fetch('/api/payments/eway/paynow/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          accessCode,
          ...bookingData
        })
      });

      const result = await response.json();
      console.log('Verification response:', result);

      if (result.success && result.payment_verified) {
        console.log('✅ Payment verified successfully!');

        // Clear stored data
        sessionStorage.removeItem('pending_eway_booking');

        if (onPaymentComplete) {
          onPaymentComplete({
            transactionId: result.transactionId,
            accessCode: accessCode,
            payment_id: result.transactionId,
            booking_id: result.appointment_id,
            status: 'completed',
            amount: paymentData.amount,
            processor: 'eway_paynow',
            appointment_details: result.appointment,
            appointment_created: result.appointment_created
          });
        }
      } else {
        throw new Error(result.message || 'Payment verification failed');
      }

    } catch (err) {
      console.error('❌ Payment verification error:', err);
      setError(`Payment verification failed: ${err.message}. Please contact support.`);

      if (onPaymentError) {
        onPaymentError(err);
      }
    } finally {
      setVerifying(false);
    }
  }, [paymentData, onPaymentComplete, onPaymentError]);

  // Check for AccessCode in URL (callback from eWay redirect)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const accessCode = urlParams.get('AccessCode') || urlParams.get('accessCode');

    if (accessCode) {
      console.log('Found AccessCode in URL, verifying payment...');
      verifyPayment(accessCode);
    }
  }, [verifyPayment]);

  useEffect(() => {
    if (!ewayConfig?.publicApiKey || !paymentData?.amount) {
      setError('Payment configuration error. Please try again.');
      return;
    }

    // Store booking data for later verification
    storeBookingData();

    // Add a small delay to ensure the container is ready
    setTimeout(() => {
      initializePayNow();
    }, 500);

    return () => {
      // Cleanup mutation observer
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [ewayConfig, paymentData, storeBookingData]);

  const initializePayNow = () => {
    try {
      console.log('Initializing eWay Pay Now button...');

      // Convert amount to cents
      const amountInCents = Math.round(paymentData.amount * 100);

      // Determine the correct script URL based on the endpoint
      const isProduction = ewayConfig.endpoint && ewayConfig.endpoint.includes('api.ewaypayments.com') && !ewayConfig.endpoint.includes('sandbox');
      const scriptUrl = isProduction
        ? 'https://secure.ewaypayments.com/scripts/eCrypt.js'
        : 'https://secure-au.sandbox.ewaypayments.com/scripts/eCrypt.js';

      console.log('Using eWay script URL:', scriptUrl, '(Production:', isProduction, ')');

      // Create result URL for callback with AccessCode
      // This URL will receive the AccessCode after payment
      const currentUrl = new URL(window.location.href);
      // Remove any existing query params and add marker
      currentUrl.search = '';
      const resultUrl = currentUrl.toString();

      console.log('Result URL for callback:', resultUrl);

      // Create a simple HTML structure with data-resulturl for proper verification
      const html = `
        <div style="text-align: center; padding: 20px;">
          <script src="${scriptUrl}"
            class="eway-paynow-button"
            data-publicapikey="${ewayConfig.publicApiKey}"
            data-amount="${amountInCents}"
            data-currency="AUD"
            data-label="Pay $${paymentData.amount}"
            data-email="${paymentData.patient_email || ''}"
            data-phone="${paymentData.patient_phone || ''}"
            data-invoicenumber="${bookingRef.current}"
            data-invoicedescription="Appointment Booking - ${paymentData.service_name || 'Consultation'}"
            data-resulturl="${resultUrl}"
            data-buttoncolor="#1976d2"
            data-buttontextcolor="#ffffff">
          </script>
        </div>
      `;

      if (containerRef.current) {
        containerRef.current.innerHTML = html;

        // Find and re-execute the script (required for dynamically added scripts)
        const scripts = containerRef.current.getElementsByTagName('script');
        for (let script of scripts) {
          const newScript = document.createElement('script');

          // Copy all attributes
          Array.from(script.attributes).forEach(attr => {
            newScript.setAttribute(attr.name, attr.value);
          });

          // Copy script content if any
          newScript.innerHTML = script.innerHTML;

          // Replace old script with new one
          script.parentNode.replaceChild(newScript, script);
        }

        console.log('eWay Pay Now button HTML inserted');

        // Check if button appears after a delay
        setTimeout(() => {
          const buttons = containerRef.current?.querySelectorAll('button, input[type="button"], input[type="submit"]');
          console.log('Found buttons:', buttons?.length || 0);
          if (buttons && buttons.length > 0) {
            setLoading(false);
            setButtonReady(true);
            console.log('✅ eWay Pay Now button is visible');
            setupPaymentCompletionWatcher();
          } else {
            console.log('❌ No buttons found - there may be an issue');
            setLoading(false);
          }
        }, 3000);
      }

    } catch (error) {
      console.error('Error initializing Pay Now:', error);
      setError('Failed to load payment system. Please try again.');
      setLoading(false);
    }
  };

  const setupPaymentCompletionWatcher = () => {
    console.log('Setting up payment completion watcher...');

    // Create mutation observer to watch for text changes in buttons
    observerRef.current = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' || mutation.type === 'characterData') {
          checkForPaymentCompletion();
        }
      });
    });

    // Start observing the container
    if (containerRef.current) {
      observerRef.current.observe(containerRef.current, {
        childList: true,
        subtree: true,
        characterData: true
      });

      // Also check periodically as fallback
      const interval = setInterval(() => {
        checkForPaymentCompletion();
      }, 2000);

      // Clean up interval after 10 minutes
      setTimeout(() => {
        clearInterval(interval);
      }, 600000);
    }
  };

  // Verify payment by InvoiceReference (used when redirect doesn't work)
  const verifyByInvoiceReference = useCallback(async () => {
    if (verificationAttemptedRef.current) {
      console.log('Verification already attempted, skipping');
      return;
    }
    verificationAttemptedRef.current = true;

    console.log('=== VERIFYING PAYMENT BY INVOICE REFERENCE ===');
    console.log('InvoiceReference:', bookingRef.current);

    setVerifying(true);

    try {
      const storedData = sessionStorage.getItem('pending_eway_booking');
      if (!storedData) {
        throw new Error('No booking data found in session');
      }

      const bookingData = JSON.parse(storedData);
      console.log('Retrieved booking data:', bookingData);

      // Call the new verification endpoint
      const response = await fetch('/api/payments/eway/paynow/verify-by-invoice', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          invoiceReference: bookingRef.current,
          ...bookingData
        })
      });

      const result = await response.json();
      console.log('Verification response:', result);

      if (result.success && result.payment_verified) {
        console.log('✅ Payment verified successfully!');
        sessionStorage.removeItem('pending_eway_booking');

        if (onPaymentComplete) {
          onPaymentComplete({
            transactionId: result.transactionId,
            invoiceReference: bookingRef.current,
            payment_id: result.transactionId,
            booking_id: result.appointment_id,
            status: 'completed',
            amount: paymentData.amount,
            processor: 'eway_paynow',
            appointment_details: result.appointment,
            appointment_created: result.appointment_created
          });
        }
      } else {
        throw new Error(result.message || 'Payment verification failed');
      }

    } catch (err) {
      console.error('❌ Payment verification error:', err);
      setError(`Payment verification failed: ${err.message}. Please contact support.`);

      if (onPaymentError) {
        onPaymentError(err);
      }
    } finally {
      setVerifying(false);
    }
  }, [paymentData, onPaymentComplete, onPaymentError]);

  const checkForPaymentCompletion = () => {
    if (paymentProcessedRef.current) {
      return; // Already processed
    }

    const buttons = containerRef.current?.querySelectorAll('button, input[type="button"], input[type="submit"]');

    for (const button of buttons || []) {
      const buttonText = button.textContent || button.innerText || '';

      // Check for completion text
      if (buttonText.includes('Processing Complete') ||
          buttonText.includes('Payment Complete') ||
          buttonText.includes('Transaction Complete') ||
          buttonText.includes('Success')) {

        console.log('🎉 Payment completion detected via button text!', buttonText);
        paymentProcessedRef.current = true;

        // Stop observing
        if (observerRef.current) {
          observerRef.current.disconnect();
        }

        // Verify payment using InvoiceReference lookup
        console.log('Verifying payment via InvoiceReference...');
        verifyByInvoiceReference();

        break;
      }
    }
  };

  // If we're verifying the payment
  if (verifying) {
    return (
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <CircularProgress size={40} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Verifying Payment...
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please wait while we confirm your payment with eWay.
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          onClick={() => {
            setError(null);
            paymentProcessedRef.current = false;
            verificationAttemptedRef.current = false;
            initializePayNow();
          }}
        >
          Try Again
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        Complete Your Payment
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Amount: <strong>${paymentData.amount}</strong>
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Click the button below to pay securely with eWay.
      </Typography>

      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, justifyContent: 'center' }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Loading payment button...</Typography>
        </Box>
      )}

      {/* Container for the eWay Pay Now button */}
      <Box
        ref={containerRef}
        sx={{
          minHeight: '100px',
          border: '1px dashed #ccc',
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      />

      {buttonReady && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Important:</strong> After completing payment, you will be redirected back to confirm your booking.
            Please do not close this window during payment.
          </Typography>
        </Alert>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
        Your payment is processed securely by eWay. We do not store your card details.
      </Typography>
    </Box>
  );
};

export default EwayPayNowForm;
